import { useState, useEffect, useCallback } from 'react';
import { Globe, Calendar, Filter, Search, X, AlertTriangle, Info, Clock, ArrowRight, Download } from 'lucide-react';
import { Layout } from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api from '../../api';
import { formatDateARG } from '../../lib/utils';

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

const DnsTraceabilityReport = () => {
  const [stats, setStats] = useState({ critical: { total: 0 }, warning: { total: 0 }, info: { total: 0 }, total_changes: 0 });
  const [changes, setChanges] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [sources, setSources] = useState([]);
  const [recordTypes, setRecordTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingChanges, setLoadingChanges] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Timeline Modal
  const [timelineModal, setTimelineModal] = useState({ open: false, fqdn: '', data: null, loading: false });
  
  // Defaults: última semana
  const defaultDateFrom = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const defaultDateTo = new Date().toISOString().split('T')[0];
  
  const [filters, setFilters] = useState({
    date_from: defaultDateFrom,
    date_to: defaultDateTo,
    source_id: '',
    severity: '',
    change_type: '',
    record_type: '',
    search: '',
    show_cosmetic: false,
    page: 1
  });

  // Fetch sources para dropdown
  useEffect(() => {
    const fetchSources = async () => {
      try {
        const res = await api.get('/reports/dns-traceability/sources');
        setSources(res.data.sources || []);
      } catch (err) {
        console.error('Error fetching DNS sources:', err);
      }
    };
    fetchSources();
  }, []);

  // Fetch record types para dropdown
  useEffect(() => {
    const fetchRecordTypes = async () => {
      try {
        const res = await api.get('/reports/dns-traceability/record-types');
        setRecordTypes(res.data.record_types || []);
      } catch (err) {
        console.error('Error fetching record types:', err);
      }
    };
    fetchRecordTypes();
  }, []);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/reports/dns-traceability/stats', {
        params: {
          date_from: filters.date_from,
          date_to: filters.date_to,
          source_id: filters.source_id || undefined,
          show_cosmetic: filters.show_cosmetic
        }
      });
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching DNS stats:', err);
    }
  }, [filters.date_from, filters.date_to, filters.source_id, filters.show_cosmetic]);

  // Fetch changes
  const fetchChanges = useCallback(async () => {
    setLoadingChanges(true);
    try {
      const res = await api.get('/reports/dns-traceability/changes', {
        params: {
          date_from: filters.date_from,
          date_to: filters.date_to,
          source_id: filters.source_id || undefined,
          severity: filters.severity || undefined,
          change_type: filters.change_type || undefined,
          record_type: filters.record_type || undefined,
          search: filters.search || undefined,
          show_cosmetic: filters.show_cosmetic,
          page: filters.page,
          page_size: 50
        }
      });
      setChanges(res.data);
    } catch (err) {
      console.error('Error fetching DNS changes:', err);
    } finally {
      setLoadingChanges(false);
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchChanges();
  }, [fetchChanges]);

  // Export to Excel
  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get('/reports/dns-traceability/export', {
        params: {
          date_from: filters.date_from,
          date_to: filters.date_to,
          source_id: filters.source_id || undefined,
          severity: filters.severity || undefined,
          change_type: filters.change_type || undefined,
          record_type: filters.record_type || undefined,
          show_cosmetic: filters.show_cosmetic
        },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trazabilidad_dns_${filters.date_from}_${filters.date_to}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting:', err);
    } finally {
      setExporting(false);
    }
  };

  // Open timeline modal
  const openTimeline = async (fqdn) => {
    setTimelineModal({ open: true, fqdn, data: null, loading: true });
    try {
      const res = await api.get(`/reports/dns-traceability/timeline/${encodeURIComponent(fqdn)}`);
      setTimelineModal(prev => ({ ...prev, data: res.data, loading: false }));
    } catch (err) {
      console.error('Error fetching timeline:', err);
      setTimelineModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Date range helpers
  const setDateRange = (days) => {
    setFilters(prev => ({
      ...prev,
      date_from: new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      date_to: new Date().toISOString().split('T')[0],
      page: 1
    }));
  };

  // Severity card click
  const handleSeverityClick = (sev) => {
    setFilters(prev => ({
      ...prev,
      severity: prev.severity === sev ? '' : sev,
      page: 1
    }));
  };

  return (
    <Layout>
    <div className="space-y-6" data-testid="dns-traceability-report">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Globe className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Trazabilidad DNS</h1>
            <p className="text-muted-foreground text-sm">Control de cambios en registros DNS</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} data-testid="export-btn">
          <Download className="h-4 w-4 mr-2" />
          {exporting ? 'Exportando...' : 'Exportar Excel'}
        </Button>
      </div>

      {/* Severity Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SeverityCard
          title="Críticos"
          severity="critical"
          icon={AlertTriangle}
          color="red"
          total={stats.critical?.total || 0}
          pending={stats.critical?.pending || 0}
          active={filters.severity === 'critical'}
          onClick={() => handleSeverityClick('critical')}
        />
        <SeverityCard
          title="Warnings"
          severity="warning"
          icon={AlertTriangle}
          color="yellow"
          total={stats.warning?.total || 0}
          pending={stats.warning?.pending || 0}
          active={filters.severity === 'warning'}
          onClick={() => handleSeverityClick('warning')}
        />
        <SeverityCard
          title="Informativos"
          severity="info"
          icon={Info}
          color="blue"
          total={stats.info?.total || 0}
          pending={stats.info?.pending || 0}
          active={filters.severity === 'info'}
          onClick={() => handleSeverityClick('info')}
        />
      </div>

      {/* Filters */}
      <FiltersBar
        filters={filters}
        setFilters={setFilters}
        sources={sources}
        recordTypes={recordTypes}
        setDateRange={setDateRange}
      />

      {/* Changes Table */}
      <ChangesTable
        changes={changes}
        loading={loading || loadingChanges}
        filters={filters}
        setFilters={setFilters}
        onOpenTimeline={openTimeline}
      />

      {/* Timeline Modal */}
      <TimelineModal
        open={timelineModal.open}
        onClose={() => setTimelineModal({ open: false, fqdn: '', data: null, loading: false })}
        fqdn={timelineModal.fqdn}
        data={timelineModal.data}
        loading={timelineModal.loading}
      />
    </div>
    </Layout>
  );
};


// ============================================
// SEVERITY CARD
// ============================================

const SeverityCard = ({ title, severity, icon: Icon, color, total, pending, active, onClick }) => {
  const colorMap = {
    red: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', icon: 'text-red-500' },
    yellow: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', icon: 'text-yellow-500' },
    blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', icon: 'text-blue-500' },
  };
  const colors = colorMap[color];

  return (
    <Card
      className={`card-subtle cursor-pointer transition-all hover:scale-[1.02] ${active ? `ring-2 ring-offset-2 ring-offset-background ${colors.border.replace('/30', '')}` : ''}`}
      onClick={onClick}
      data-testid={`severity-card-${severity}`}
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Icon className={`h-4 w-4 ${colors.icon}`} />
            {title}
          </span>
          {pending > 0 && (
            <Badge variant="outline" className={`${colors.bg} ${colors.text} ${colors.border}`}>
              {pending} pendientes
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold">{total}</span>
          <span className="text-sm text-muted-foreground">cambios</span>
        </div>
      </CardContent>
    </Card>
  );
};


// ============================================
// FILTERS BAR
// ============================================

const FiltersBar = ({ filters, setFilters, sources, recordTypes, setDateRange }) => {
  return (
    <Card className="card-subtle">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Icon + Label */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Filtros:</span>
          </div>

          {/* Date Range */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <Input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value, page: 1 }))}
              className="h-9 w-36"
              data-testid="date-from-input"
            />
            <span className="text-muted-foreground">a</span>
            <Input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value, page: 1 }))}
              className="h-9 w-36"
              data-testid="date-to-input"
            />
          </div>

          {/* Quick Date Buttons */}
          <div className="flex items-center gap-1 border-l pl-3">
            <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setDateRange(7)}>
              7 días
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setDateRange(30)}>
              1 mes
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setDateRange(90)}>
              3 meses
            </Button>
          </div>

          {/* Source Dropdown */}
          <Select
            value={filters.source_id || 'all'}
            onValueChange={(val) => setFilters(prev => ({ ...prev, source_id: val === 'all' ? '' : val, page: 1 }))}
          >
            <SelectTrigger className="h-9 w-48" data-testid="source-select">
              <SelectValue placeholder="Todas las fuentes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las fuentes</SelectItem>
              {sources.map(s => (
                <SelectItem key={s.id} value={String(s.id)}>{s.name} ({s.domain})</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Change Type Dropdown */}
          <Select
            value={filters.change_type || 'all'}
            onValueChange={(val) => setFilters(prev => ({ ...prev, change_type: val === 'all' ? '' : val, page: 1 }))}
          >
            <SelectTrigger className="h-9 w-36" data-testid="change-type-select">
              <SelectValue placeholder="Tipo cambio" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="added">Agregados</SelectItem>
              <SelectItem value="removed">Eliminados</SelectItem>
              <SelectItem value="modified">Modificados</SelectItem>
            </SelectContent>
          </Select>

          {/* Record Type Dropdown */}
          <Select
            value={filters.record_type || 'all'}
            onValueChange={(val) => setFilters(prev => ({ ...prev, record_type: val === 'all' ? '' : val, page: 1 }))}
          >
            <SelectTrigger className="h-9 w-28" data-testid="record-type-select">
              <SelectValue placeholder="Tipo DNS" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {recordTypes.map(rt => (
                <SelectItem key={rt} value={rt}>{rt}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Search */}
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar FQDN..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
              className="h-9 w-48"
              data-testid="search-input"
            />
          </div>

          {/* Clear Filters */}
          {(filters.search || filters.change_type || filters.severity || filters.source_id || filters.record_type) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilters(prev => ({ ...prev, search: '', change_type: '', severity: '', source_id: '', record_type: '', page: 1 }))}
            >
              <X className="h-4 w-4 mr-1" /> Limpiar
            </Button>
          )}
        </div>

        {/* Toggle row */}
        <div className="flex items-center gap-6 mt-4 pt-4 border-t border-border/50">
          {/* Show Cosmetic (TTL) Toggle */}
          <div className="flex items-center gap-2">
            <Switch
              id="show-cosmetic"
              checked={filters.show_cosmetic}
              onCheckedChange={(checked) => setFilters(prev => ({ ...prev, show_cosmetic: checked, page: 1 }))}
              data-testid="show-cosmetic-toggle"
            />
            <Label htmlFor="show-cosmetic" className="text-sm cursor-pointer">
              Mostrar cambios TTL (cosméticos)
            </Label>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};


// ============================================
// CHANGES TABLE
// ============================================

const ChangesTable = ({ changes, loading, filters, setFilters, onAcknowledge, onOpenTimeline }) => {
  const getSeverityBadge = (severity) => {
    const styles = {
      critical: 'bg-red-500/10 text-red-400 border-red-500/30',
      warning: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
      info: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    };
    const icons = {
      critical: <AlertTriangle className="h-3 w-3 mr-1" />,
      warning: <AlertTriangle className="h-3 w-3 mr-1" />,
      info: <Info className="h-3 w-3 mr-1" />,
    };
    return (
      <Badge variant="outline" className={`${styles[severity]} flex items-center`}>
        {icons[severity]}
        {severity}
      </Badge>
    );
  };

  const getChangeBadge = (type) => {
    const styles = {
      added: 'bg-green-500/10 text-green-400 border-green-500/30',
      removed: 'bg-red-500/10 text-red-400 border-red-500/30',
      modified: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
    };
    const labels = { added: 'Agregado', removed: 'Eliminado', modified: 'Modificado' };
    return <Badge variant="outline" className={styles[type]}>{labels[type]}</Badge>;
  };

  const renderValueChange = (item) => {
    if (item.change_type === 'added') {
      return (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">—</span>
          <ArrowRight className="h-3 w-3 text-muted-foreground" />
          <span className="font-mono text-green-400 max-w-xs truncate" title={item.new_value}>
            {item.new_value || '(vacío)'}
          </span>
        </div>
      );
    }
    if (item.change_type === 'removed') {
      return (
        <div className="flex items-center gap-2 text-xs">
          <span className="font-mono text-red-400 max-w-xs truncate" title={item.old_value}>
            {item.old_value || '(vacío)'}
          </span>
          <ArrowRight className="h-3 w-3 text-muted-foreground" />
          <span className="text-muted-foreground">—</span>
        </div>
      );
    }
    // modified
    return (
      <div className="flex items-center gap-2 text-xs">
        <span className={`font-mono max-w-[120px] truncate ${item.is_cosmetic ? 'text-muted-foreground' : 'text-red-400'}`} title={item.old_value}>
          {item.old_value || '(vacío)'}
        </span>
        <ArrowRight className={`h-3 w-3 ${item.is_cosmetic ? 'text-muted-foreground' : 'text-primary'}`} />
        <span className={`font-mono max-w-[120px] truncate ${item.is_cosmetic ? 'text-muted-foreground' : 'text-green-400'}`} title={item.new_value}>
          {item.new_value || '(vacío)'}
        </span>
        {item.is_cosmetic && (
          <Badge variant="outline" className="bg-gray-500/10 text-gray-400 border-gray-500/30 text-[10px] px-1">
            TTL
          </Badge>
        )}
      </div>
    );
  };

  return (
    <Card className="card-subtle">
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-primary" />
            Cambios DNS ({changes.total})
          </span>
          <span className="text-sm font-normal text-muted-foreground">
            Página {changes.page} de {changes.pages}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Cargando...</div>
        ) : changes.items.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No hay cambios DNS en el período seleccionado con los filtros actuales
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="changes-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3">Fecha</th>
                    <th className="text-left p-3">Source</th>
                    <th className="text-left p-3">FQDN</th>
                    <th className="text-left p-3">Tipo</th>
                    <th className="text-left p-3">Cambio</th>
                    <th className="text-left p-3">Valor</th>
                    <th className="text-left p-3">Severity</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.items.map((item) => (
                    <tr
                      key={item.id}
                      className="border-b border-border/50 hover:bg-muted/30"
                      data-testid={`change-row-${item.id}`}
                    >
                      <td className="p-3 text-muted-foreground whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDateARG(item.detected_at)}
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="text-xs">{item.source_name}</span>
                      </td>
                      <td className="p-3">
                        <button
                          onClick={() => onOpenTimeline(item.record_fqdn)}
                          className="font-mono text-xs text-primary hover:underline cursor-pointer text-left"
                          data-testid={`fqdn-link-${item.id}`}
                        >
                          {item.record_fqdn}
                        </button>
                      </td>
                      <td className="p-3">
                        <Badge variant="secondary" className="text-xs">
                          {item.record_type}
                        </Badge>
                      </td>
                      <td className="p-3">{getChangeBadge(item.change_type)}</td>
                      <td className="p-3">{renderValueChange(item)}</td>
                      <td className="p-3">{getSeverityBadge(item.severity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {changes.pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-border/50">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filters.page <= 1}
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Anterior
                </Button>
                <span className="text-sm text-muted-foreground">
                  Página {filters.page} de {changes.pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filters.page >= changes.pages}
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Siguiente
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};


// ============================================
// TIMELINE MODAL
// ============================================

const TimelineModal = ({ open, onClose, fqdn, data, loading }) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-primary" />
            Timeline: <span className="font-mono text-sm">{fqdn}</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Cargando timeline...</div>
          ) : !data ? (
            <div className="text-center py-8 text-muted-foreground">No se pudo cargar el timeline</div>
          ) : (
            <div className="space-y-6">
              {/* Changes Section */}
              {data.changes && data.changes.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-primary" />
                    Cambios detectados ({data.changes.length})
                  </h3>
                  <div className="space-y-2">
                    {data.changes.map((c, idx) => (
                      <div
                        key={c.id || idx}
                        className={`p-3 rounded-lg border ${c.is_cosmetic ? 'bg-muted/30 border-border/50' : 'bg-muted/50 border-border'}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={
                              c.change_type === 'added' ? 'bg-green-500/10 text-green-400 border-green-500/30' :
                              c.change_type === 'removed' ? 'bg-red-500/10 text-red-400 border-red-500/30' :
                              'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'
                            }>
                              {c.change_type}
                            </Badge>
                            <Badge variant="outline" className={
                              c.severity === 'critical' ? 'bg-red-500/10 text-red-400 border-red-500/30' :
                              c.severity === 'warning' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30' :
                              'bg-blue-500/10 text-blue-400 border-blue-500/30'
                            }>
                              {c.severity}
                            </Badge>
                            {c.is_cosmetic && (
                              <Badge variant="outline" className="bg-gray-500/10 text-gray-400 border-gray-500/30">
                                Solo TTL
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {formatDateARG(c.detected_at)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-mono text-red-400">{c.old_value || '—'}</span>
                          <ArrowRight className="h-4 w-4 text-muted-foreground" />
                          <span className="font-mono text-green-400">{c.new_value || '—'}</span>
                        </div>
                        {c.old_ttl !== c.new_ttl && (
                          <div className="text-xs text-muted-foreground mt-1">
                            TTL: {c.old_ttl || '—'} → {c.new_ttl || '—'}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* History Section */}
              {data.history && data.history.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary" />
                    Historial de snapshots ({data.history.length})
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left p-2">Fecha</th>
                          <th className="text-left p-2">Tipo</th>
                          <th className="text-left p-2">Valor</th>
                          <th className="text-left p-2">TTL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.history.map((h, idx) => (
                          <tr key={h.id || idx} className="border-b border-border/50">
                            <td className="p-2 text-muted-foreground text-xs">
                              {formatDateARG(h.snapshot_at)}
                            </td>
                            <td className="p-2">
                              <Badge variant="secondary" className="text-xs">{h.record_type}</Badge>
                            </td>
                            <td className="p-2 font-mono text-xs">{h.record_value}</td>
                            <td className="p-2 text-muted-foreground">{h.ttl || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Empty state */}
              {(!data.changes || data.changes.length === 0) && (!data.history || data.history.length === 0) && (
                <div className="text-center py-8 text-muted-foreground">
                  No hay datos históricos para este registro
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};


export default DnsTraceabilityReport;
